import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ErrorPanel } from '../../../../../../common/widgets/ErrorPanel';
import Loader from '../../../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../../../common/widgets/LoadingSpinner';
import { ErrorType } from '../../../../models/Errors';
import { resources } from '../../../../resources/resources';
import { employeeBenefitsService } from '../../../../services/employee-choices';
import NotFound from '../../../ProductsPage/components/NotFound';
import ProductCart from '../Components/ProductCart';
import { BenefitInfoState } from './BenefitInfoState';

export default function BenefitInfoPage() {
  const { t: translate } = useTranslation();
  const navigate = useNavigate();
  const { orderId: orderIdAsString } = useParams();

  const [state, setState] = useState<BenefitInfoState>({} as BenefitInfoState);

  const loadBenefitInfo = useCallback(async (orderId: number) => {
    const response = await employeeBenefitsService.getBenefitInfo(orderId);
    if (!response.hasErrors) {
      setState(
        produce(d => {
          d.data = response.data;
        }),
      );
    } else {
      setState(
        produce(d => {
          d.errorType = response.errorType;
        }),
      );
    }
  }, []);

  useEffect(() => {
    const orderId = Number(orderIdAsString);
    if (!_.isNaN(orderId)) {
      loadBenefitInfo(orderId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderIdAsString, state.reload]);

  if (state.errorType === ErrorType.NotFoundError) return <NotFound />;
  if (state.errorType === ErrorType.InternalServerError) return <ErrorPanel />;

  return (
    <div className="panel padding-xl benefits-section">
      <Loader loading={!state.data} loader={<LoadingSpinner />}>
        <div className="btn-panel-back-wrapper">
          <button className="btn btn-panel-back" onClick={() => navigate(-1)}>
            <span className="btn-text">
              {translate(resources.admin.employeeBenefits.title)}
            </span>
          </button>
        </div>

        {/*Order section*/}
        <h1>
          {translate(resources.admin.employeeBenefits.details.orderSection)}
        </h1>

        <div className="order-details-panel admin-order">
          <ul>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.employee)}:
              </span>{' '}
              <span>{state.data?.employee}</span>
            </li>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.phoneNo)}:
              </span>{' '}
              <span>{state.data?.phoneNo}</span>
            </li>
          </ul>
          <ul>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.email)}:
              </span>{' '}
              <span>{state.data?.email}</span>
            </li>
            <li>
              <span>
                {translate(
                  resources.admin.employeeBenefits.details.dateMidified,
                )}
                :
              </span>{' '}
              <span>
                {moment(new Date(state.data?.dateModified ?? '')).format(
                  'DD.MM.YYYY',
                )}
              </span>
            </li>
          </ul>
        </div>

        {/*Payments and products section*/}
        <hr className="admin-prod-separator" />
        <h1>
          {translate(
            resources.admin.employeeBenefits.details.paymentAndProductsSection,
          )}
        </h1>
        <div className="order-details-panel admin-payment">
          <ul>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.orderId)}:
              </span>{' '}
              <span>#{state.data?.orderId}</span>
            </li>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.orderDate)}:
              </span>{' '}
              <span>
                {moment(new Date(state.data?.dateAdded ?? '')).format(
                  'DD.MM.YYYY',
                )}
              </span>
            </li>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.orderState)}
                :
              </span>{' '}
              <span>{state.data?.orderStatus}</span>
            </li>
            <li>
              <span>
                {translate(
                  resources.admin.employeeBenefits.details.paymentMethod,
                )}
                :
              </span>{' '}
              <span>{state.data?.paymentMethod}</span>
            </li>
          </ul>
          <ul>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.total)}:
              </span>{' '}
              <span>{state.data?.totalProducts}</span>
            </li>
            <li>
              <span>
                {translate(
                  resources.admin.employeeBenefits.details.paydFromBudget,
                )}
                :
              </span>{' '}
              <span>{state.data?.paidFromBudget}</span>
            </li>
            <li>
              <span>
                {translate(
                  resources.admin.employeeBenefits.details.contribution,
                )}
                :
              </span>{' '}
              <span>{state.data?.contribution}</span>
            </li>
            <li>
              <span>
                {translate(resources.admin.employeeBenefits.details.payrollTax)}
                :
              </span>{' '}
              <span>{state.data?.payrollTax}</span>
            </li>
          </ul>
        </div>

        <div className="order-product-list benefit-info-page">
          {state.data?.products.map(p => (
            <ProductCart
              key={p.orderProductId}
              orderId={state.data?.orderId ?? 0}
              product={p}
              setState={setState}
            />
          ))}
        </div>
      </Loader>
    </div>
  );
}
